/**
 * SimplyLoan 24-Month Cohort & Email LTV API Client (Norge + Sverige)
 */
import { API_BASE_URL, SMS_SCHEDULER_API_URL } from '../config';
import snapshotData from '../data/simplyloan_ltv_snapshot.json';

export type LtvCountry = 'all' | 'no' | 'se';
export type LtvAttributionMode = 'strict_lead' | 'standard' | 'blended';

export interface LtvPartnerStat {
  partner: string;
  revenue_dkk: number;
  conversions: number;
}

export interface LtvCampaignStat {
  platform: 'meta' | 'microsoft' | 'google' | string;
  campaign_id: string;
  campaign_name: string;
  spend_dkk: number;
  clicks: number;
  impressions: number;
  platform_leads: number;
  country?: string;
}

export interface LtvEmailStep {
  offset: number;
  label: string;
  short_label: string;
  calendar_month: string;
  calendar_month_label: string;
  calendar_month_full: string;
  email_revenue_dkk: number;
  email_conversions: number;
  strict_email_revenue_dkk?: number;
  strict_email_conversions?: number;
  allocated_email_revenue_dkk?: number;
  allocated_return_web_dkk?: number;
  blended_step_revenue_dkk?: number;
  incremental_roas: number | null;
  incremental_roas_pct: number | null;
  blended_incremental_roas_pct?: number | null;
  cumulative_email_revenue_dkk: number;
  strict_cumulative_email_revenue_dkk?: number;
  blended_cumulative_email_revenue_dkk?: number;
  cumulative_total_revenue_dkk: number;
  blended_cumulative_total_revenue_dkk?: number;
  strict_cumulative_total_revenue_dkk?: number;
  cumulative_roas: number | null;
  cumulative_roas_pct: number | null;
  blended_cumulative_roas_pct?: number | null;
  strict_cumulative_roas_pct?: number | null;
  cumulative_ltv_per_lead_dkk: number;
  blended_cumulative_ltv_per_lead_dkk?: number;
  cumulative_profit_dkk: number;
  blended_cumulative_profit_dkk?: number;
  partners: LtvPartnerStat[];
}

export interface LtvCohortRow {
  month: string;
  month_label: string;
  month_short: string;
  country: LtvCountry;
  in_last_24m: boolean;
  is_valid: boolean;
  has_ad_spend: boolean;
  months_tracked: number;
  breakeven_offset: number | null;
  blended_breakeven_offset?: number | null;
  // Leads
  unique_leads: number;
  paid_leads: number;
  organic_leads: number;
  total_submissions: number;
  non_lead_clicks?: number;
  lead_conversion_rate_pct?: number | null;
  // Spend
  spend_dkk: number;
  meta_spend_dkk: number;
  microsoft_spend_dkk: number;
  google_spend_dkk: number;
  clicks: number;
  impressions: number;
  platform_leads: number;
  cpl_dkk: number | null;
  paid_cpl_dkk: number | null;
  campaigns: LtvCampaignStat[];
  // Immediate (Month 0 on Ad / Website)
  immediate_revenue_dkk: number;
  immediate_paid_strict_dkk: number;
  immediate_web_organic_dkk: number;
  immediate_direct_lead_dkk?: number;
  immediate_direct_lead_conversions?: number;
  immediate_ad_tag_no_lead_dkk?: number;
  immediate_ad_tag_no_lead_conversions?: number;
  immediate_wall_no_lead_dkk?: number;
  immediate_wall_no_lead_conversions?: number;
  immediate_unmatched_total_dkk?: number;
  immediate_conversions: number;
  immediate_roas: number | null;
  immediate_roas_pct: number | null;
  immediate_strict_roas: number | null;
  immediate_strict_roas_pct: number | null;
  immediate_direct_lead_roas_pct?: number | null;
  immediate_epc_per_lead_dkk: number;
  immediate_profit_dkk: number;
  immediate_partners: LtvPartnerStat[];
  // Month 0 Combined (Immediate + M0 Email)
  m0_email_revenue_dkk: number;
  m0_blended_email_revenue_dkk?: number;
  m0_total_revenue_dkk: number;
  m0_total_roas: number | null;
  m0_total_roas_pct: number | null;
  // Subsequent Email ("Alle måneder efter")
  subsequent_email_revenue_dkk: number;
  subsequent_blended_email_revenue_dkk?: number;
  total_email_revenue_dkk: number;
  total_email_conversions: number;
  strict_email_revenue_dkk?: number;
  strict_email_conversions?: number;
  allocated_unmatched_email_dkk?: number;
  allocated_unmatched_email_conversions?: number;
  allocated_return_web_dkk?: number;
  allocated_return_web_conversions?: number;
  allocated_hidden_ltv_dkk?: number;
  blended_email_revenue_dkk?: number;
  email_roas_lift: number | null;
  email_roas_lift_pct: number | null;
  blended_email_roas_lift_pct?: number | null;
  email_ltv_per_lead_dkk: number;
  blended_email_ltv_per_lead_dkk?: number;
  email_share_of_ltv_pct: number;
  email_timeline: LtvEmailStep[];
  // Total LTV (Standard vs Blended vs Strict 1:1 Lead)
  total_ltv_revenue_dkk: number;
  total_ltv_conversions: number;
  total_ltv_roas: number | null;
  total_ltv_roas_pct: number | null;
  roas_multiplier: number | null;
  ltv_per_lead_dkk: number;
  net_profit_dkk: number;
  // Blended
  blended_ltv_revenue_dkk?: number;
  blended_ltv_roas?: number | null;
  blended_ltv_roas_pct?: number | null;
  blended_roas_multiplier?: number | null;
  blended_ltv_per_lead_dkk?: number;
  blended_net_profit_dkk?: number;
  // Strict 1:1 LeadID
  strict_lead_ltv_revenue_dkk?: number;
  strict_lead_ltv_roas?: number | null;
  strict_lead_ltv_roas_pct?: number | null;
  strict_lead_roas_multiplier?: number | null;
  strict_lead_ltv_per_lead_dkk?: number;
  strict_lead_net_profit_dkk?: number;
}

export interface LtvSummaryStats {
  cohorts_count: number;
  unique_leads: number;
  paid_leads: number;
  clicks?: number;
  non_lead_clicks?: number;
  spend_dkk: number;
  meta_spend_dkk: number;
  microsoft_spend_dkk: number;
  cpl_dkk: number | null;
  immediate_revenue_dkk: number;
  immediate_paid_strict_dkk: number;
  immediate_direct_lead_dkk?: number;
  immediate_unmatched_total_dkk?: number;
  immediate_conversions: number;
  immediate_roas: number | null;
  immediate_roas_pct: number | null;
  immediate_strict_roas_pct: number | null;
  immediate_direct_lead_roas_pct?: number | null;
  m0_email_revenue_dkk: number;
  subsequent_email_revenue_dkk: number;
  total_email_revenue_dkk: number;
  total_email_conversions: number;
  strict_email_revenue_dkk?: number;
  strict_email_conversions?: number;
  strict_email_roas_lift_pct?: number | null;
  allocated_unmatched_email_dkk?: number;
  allocated_return_web_dkk?: number;
  allocated_hidden_ltv_dkk?: number;
  blended_email_revenue_dkk?: number;
  email_roas_lift: number | null;
  email_roas_lift_pct: number | null;
  blended_email_roas_lift_pct?: number | null;
  total_ltv_revenue_dkk: number;
  total_ltv_conversions: number;
  total_ltv_roas: number | null;
  total_ltv_roas_pct: number | null;
  roas_multiplier: number | null;
  ltv_per_lead_dkk: number;
  immediate_per_lead_dkk: number;
  email_per_lead_dkk: number;
  net_profit_dkk: number;
  // Blended
  blended_ltv_revenue_dkk?: number;
  blended_ltv_roas?: number | null;
  blended_ltv_roas_pct?: number | null;
  blended_roas_multiplier?: number | null;
  blended_ltv_per_lead_dkk?: number;
  blended_email_per_lead_dkk?: number;
  blended_net_profit_dkk?: number;
  // Strict 1:1 LeadID
  strict_lead_ltv_revenue_dkk?: number;
  strict_lead_ltv_roas?: number | null;
  strict_lead_ltv_roas_pct?: number | null;
  strict_lead_roas_multiplier?: number | null;
  strict_lead_ltv_per_lead_dkk?: number;
  strict_lead_immediate_per_lead_dkk?: number;
  strict_lead_email_per_lead_dkk?: number;
  strict_lead_net_profit_dkk?: number;
}

export interface LtvMaturationPoint {
  offset: number;
  label: string;
  paid_cohorts_count: number;
  email_revenue_dkk: number;
  blended_email_revenue_dkk?: number;
  avg_cumulative_roas_pct: number;
  avg_blended_cumulative_roas_pct?: number;
  avg_strict_cumulative_roas_pct?: number;
}

export interface LtvCalendarEmailMonth {
  month: string;
  month_label: string;
  native_lead_email_dkk: number;
  unmatched_email_dkk?: number;
  prior_window_email_dkk?: number;
  coreg_email_dkk: number;
  total_email_dkk: number;
  conversions: number;
}

export interface LtvUnmatchedTrafficAnalysis {
  traffic_funnel: {
    ad_clicks: number;
    unique_leads_paid_months: number;
    unique_leads_all_months: number;
    non_lead_clicks: number;
    lead_conversion_rate_pct: number;
    non_lead_click_share_pct: number;
  };
  immediate_breakdown: {
    direct_lead_dkk: number;
    direct_lead_conversions: number;
    direct_lead_paid_months_dkk: number;
    ad_tag_no_lead_dkk: number;
    ad_tag_no_lead_conversions: number;
    wall_paid_month_no_lead_dkk: number;
    wall_paid_month_no_lead_conversions: number;
    zero_spend_return_web_dkk: number;
    zero_spend_return_web_conversions: number;
    total_immediate_dkk: number;
    total_immediate_conversions: number;
    unmatched_immediate_paid_months_dkk: number;
    unmatched_share_of_immediate_pct: number;
  };
  email_breakdown: {
    direct_lead_paid_cohorts_dkk: number;
    direct_lead_paid_cohorts_conversions: number;
    direct_lead_all_cohorts_dkk: number;
    direct_lead_all_cohorts_conversions: number;
    unmatched_revisit_dkk: number;
    unmatched_revisit_conversions: number;
    unmatched_sources: { source: string; revenue_dkk: number; conversions: number }[];
    prior_window_leads_dkk: number;
    prior_window_leads_conversions: number;
    coreg_bonus_dkk: number;
    total_email_channel_dkk: number;
  };
}

export interface LtvScopePayload {
  country: LtvCountry;
  start_month: string;
  end_month: string;
  generated_at: string;
  summary_all_valid: LtvSummaryStats;
  summary_paid_cohorts: LtvSummaryStats;
  unmatched_traffic_analysis?: LtvUnmatchedTrafficAnalysis;
  email_channel_breakdown: {
    native_simplyloan_leads_email_dkk: number;
    unmatched_revisit_email_dkk?: number;
    prior_window_leads_email_dkk?: number;
    coreg_imported_email_dkk: number;
    total_email_channel_dkk: number;
    by_calendar_month: LtvCalendarEmailMonth[];
  };
  maturation_curve: LtvMaturationPoint[];
  cohorts: LtvCohortRow[];
}

export interface LtvApiResponse {
  success: boolean;
  cached: boolean;
  selected_country: LtvCountry;
  selected_month?: string | null;
  data: LtvScopePayload;
  country_comparison: {
    no: LtvSummaryStats;
    se: LtvSummaryStats;
    all: LtvSummaryStats;
  };
}

export function getInitialLtvResponse(country: LtvCountry = 'all'): LtvApiResponse {
  const raw = snapshotData as unknown as {
    country_comparison: LtvApiResponse['country_comparison'];
    scopes: Record<LtvCountry, LtvScopePayload>;
  };
  return {
    success: true,
    cached: true,
    selected_country: country,
    data: raw.scopes[country] || raw.scopes.all,
    country_comparison: raw.country_comparison,
  };
}

export async function fetchSimplyloanLtvCohorts(params: {
  country: LtvCountry;
  refresh?: boolean;
  syncAds?: boolean;
  signal?: AbortSignal;
}): Promise<LtvApiResponse> {
  const qs = new URLSearchParams({
    country: params.country,
    ...(params.refresh ? { refresh: 'true' } : {}),
    ...(params.syncAds ? { sync_ads: 'true' } : {}),
  });

  const urls = [
    `${SMS_SCHEDULER_API_URL}/api/simplyloan/ltv-cohorts?${qs.toString()}`,
    `${API_BASE_URL}/api/simplyloan/ltv-cohorts?${qs.toString()}`,
  ];

  for (const url of urls) {
    try {
      const res = await fetch(url, { signal: params.signal });
      if (res.ok) {
        const json = await res.json();
        if (json && json.data && json.country_comparison) {
          return json as LtvApiResponse;
        }
      }
    } catch {
      // Fall through to next endpoint or initial snapshot
    }
  }

  return getInitialLtvResponse(params.country);
}
